/**
 * Los datos que el negocio exige en cada cliente (lo pidió el dueño,
 * 2026-10-05). Se eligen en Clientes → Opciones y se guardan en
 * `settings.cliente_obligatorios`.
 *
 * ⚠️ Es la MISMA regla que `pos/clientes_obligatorios.py` en el servidor, que
 * la hace cumplir al crear desde Clientes, al editar y al importar. En la caja
 * la aplica SOLO el formulario del aparato, antes de guardar: lo registrado
 * sin internet no se rechaza al sincronizar, porque perdería al cliente de una
 * venta ya hecha (ver el archivo del servidor).
 *
 * Es la hermana de `lib/obligatorios.ts` (los de productos).
 */

import { useMemo } from "react";
import { useQuery } from "@tanstack/react-query";
import { negocio } from "./endpoints";

export type DatoDeCliente = "documento" | "telefono" | "correo" | "direccion";

export const DATOS_DE_CLIENTE: { valor: DatoDeCliente; etiqueta: string; ayuda: string }[] = [
  {
    valor: "documento",
    etiqueta: "Documento de identidad",
    ayuda: "La cédula, el RIF o el documento que corresponda en el país.",
  },
  { valor: "telefono", etiqueta: "Teléfono", ayuda: "Para contactar al cliente por WhatsApp." },
  { valor: "correo", etiqueta: "Correo electrónico", ayuda: "Para enviarle documentos." },
  { valor: "direccion", etiqueta: "Dirección", ayuda: "Para entregas a domicilio." },
];

const NOMBRE: Record<DatoDeCliente, string> = Object.fromEntries(
  DATOS_DE_CLIENTE.map((d) => [d.valor, d.etiqueta]),
) as Record<DatoDeCliente, string>;

/** Lo que exige el negocio, en el orden de `DATOS_DE_CLIENTE`. */
export function datosExigidos(lista: string[] | undefined): DatoDeCliente[] {
  const elegidos = lista ?? [];
  return DATOS_DE_CLIENTE.map((d) => d.valor).filter((v) => elegidos.includes(v));
}

/**
 * Lo que exige este negocio, leído de los ajustes.
 *
 * Usa la consulta `["negocio"]`, que la caja ya tiene guardada en el aparato:
 * sin internet sigue sabiendo qué pedir.
 */
export function useDatosExigidos(): DatoDeCliente[] {
  const { data } = useQuery({
    queryKey: ["negocio"],
    queryFn: negocio.obtener,
    staleTime: 5 * 60 * 1000,
  });
  const lista = data?.settings?.cliente_obligatorios;
  return useMemo(() => datosExigidos(lista), [lista]);
}

const vacio = (v: string | null | undefined) => !v || !v.trim();

/** Lo que falta, de lo que exige el negocio. */
export function faltanEnCliente(
  exigidos: DatoDeCliente[],
  datos: { documento: string; telefono: string; correo: string; direccion: string },
): DatoDeCliente[] {
  return exigidos.filter((d) => vacio(datos[d]));
}

export function nombresDeDatos(faltan: DatoDeCliente[]): string {
  return faltan.map((f) => NOMBRE[f]).join(", ");
}

/** El aviso de cuando falta algo, con las mismas palabras que el servidor. */
export function mensajeDeDatosFaltantes(faltan: DatoDeCliente[]): string {
  return `Faltan datos obligatorios: ${nombresDeDatos(faltan)}. Este negocio los exige en todos sus clientes.`;
}
